import React, { useState, useRef ,useEffect} from "react";
import "./ClaimRequest.scss";
import InstituteApi from "Api/InstituteApi";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Col,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Table,
} from "reactstrap";
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb";
import DataTableLoader from "SkeletonLoader/DataTableLoader";
import { Toast } from "primereact/toast";
import { StatusBadge } from "components/Common";   


const BusinessUserList = () => {
    document.title = "Claim Requests";
    const navigate = useNavigate();
    const accessToken = localStorage.getItem("accessToken");
    const userData = JSON.parse(localStorage.getItem("userData"));
    const permissions = userData?.permission || [];
    const [loading, setLoading] = useState(true);
    const toast = useRef(null);
    
    const breadcrumbItems = [
        { label: "Pixwik", link: "/" },
        { label: "Users", link: "/user-list" },
        { label: "Claim Requests", link: "#" },
    ];

    const handleBreadcrumbClick = link => {
        if (link !== "#") {
          navigate(link)
        }
    }

    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [limit] = useState(10);
    const [claimRequests, setClaimRequests] = useState([]);

    const totalPages = Math.ceil(totalRecords / limit);

    const getClaimRequests = () => {
        setLoading(true);
        const config = {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            }
        };
        InstituteApi.getClaimRequestInstitution(config)
            .then((response) => {
                setLoading(false);
                const requests = Array.isArray(response.data) ? response.data : [];
                setClaimRequests(requests);
                setTotalRecords(requests.length);
            })
            .catch((error) => {
                console.error("Error fetching claim requests:", error);
                setLoading(false);
                setClaimRequests([]);
                setTotalRecords(0);
                if (toast.current) {
                    toast.current.show({ 
                        severity: 'error', 
                        summary: 'Error', 
                        detail: 'Failed to fetch claim requests. Please try again later.' 
                    });
                }
            });
    }

    useEffect(() => {
        getClaimRequests();
    }, [accessToken]);

    const hasPermission = (permissions, resource, method) => {
        if (!Array.isArray(permissions.permission)) {
            console.error("Permissions is not an array:", permissions);
            return false; 
        }
      
        return permissions.permission.some(
            (perm) =>
                perm.permissionName === resource &&
                perm.permissionType.includes(method)
        );
    };

    const handleViewRequest = (request) => {
        navigate(`/verify-business-user/${request.businessId}`);
    };

    return (
        <>

            <Row>
                <Col xs="12">
                    <div className="Business-User-List-header d-flex align-items-center justify-content-between">
                        <CustomBreadCrumb 
                            data={breadcrumbItems} 
                            pageTitle="Claim Requests" 
                            handleClick={handleBreadcrumbClick}
                        />
                    </div>
                    {(userData.role==='admin' || hasPermission(permissions,"businessUsers","GET"))&&(
                        <div className="data-table-container">
                            <div className="table-responsive mt-4 mb-3">
                                {loading ? (
                                    <DataTableLoader cols={6} />
                                ) : (
                                    <Table className="table mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Business Name</th>
                                            <th>Owner Name</th>
                                            <th>Email</th>
                                            <th>Status</th>
                                            <th>Claimed</th>
                                            <th>Verify</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {claimRequests.map((request, index) => (
                                            <tr key={request._id} className="Business-user-table-row">
                                                <th scope="row">{index + 1}</th>
                                                <td>{request.businessName}</td>
                                                <td>{request.verificationDetails?.businessOwner}</td>
                                                <td>{request.verificationDetails?.businessOwnerEmail}</td>
                                                <td>
                                                    <StatusBadge 
                                                        status={request.status}
                                                    />
                                                </td>
                                                <td>
                                                    <StatusBadge 
                                                        status={request.isClaimed ? "active" : "inactive"}
                                                        customText={request.isClaimed ? "Yes" : "No"}
                                                    />
                                                </td>
                                                <td>
                                                    <Button color="primary" size="sm" onClick={() => handleViewRequest(request)}>
                                                        Verify
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    </Table>
                                )}
                            </div>
                        </div>
                    )}

                    {(userData.role==='admin' || hasPermission(permissions,"businessUsers","GET")) && totalPages > 1 && (
                        <div className="pagination-container">
                            <Pagination>
                                <PaginationItem disabled={page <= 1}>
                                    <PaginationLink previous onClick={() => setPage(page - 1)} />
                                </PaginationItem>
                                
                                {[...Array(Math.min(3, totalPages))].map((_, index) => (
                                    <PaginationItem key={index} active={index + 1 === page}>
                                        <PaginationLink onClick={() => setPage(index + 1)}>
                                            {index + 1}
                                        </PaginationLink>
                                    </PaginationItem>
                                ))}

                                {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                                    <PaginationItem disabled>
                                        <PaginationLink>...</PaginationLink>
                                    </PaginationItem>
                                )}

                                {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                                    <PaginationItem active>
                                        <PaginationLink onClick={() => setPage(page)}>
                                            {page}
                                        </PaginationLink>
                                    </PaginationItem>
                                )}

                                {totalPages > 6 && page < totalPages - 2 && (
                                    <PaginationItem disabled>
                                        <PaginationLink>...</PaginationLink>
                                    </PaginationItem>
                                )}

                                {totalPages > 3 &&
                                    [...Array(Math.min(3, totalPages))].map((_, index) => {
                                        const pageNumber = totalPages - 2 + index;
                                        return (
                                            <PaginationItem
                                                key={pageNumber}
                                                active={pageNumber === page}
                                            >
                                                <PaginationLink onClick={() => setPage(pageNumber)}>
                                                    {pageNumber}
                                                </PaginationLink>
                                            </PaginationItem>
                                        );
                                    })}

                                <PaginationItem disabled={page >= totalPages}>
                                    <PaginationLink next onClick={() => setPage(page + 1)} />
                                </PaginationItem>
                            </Pagination>
                        </div>
                    )}
                </Col>
            </Row>
            <Toast ref={toast} />
        </>
    );
};

export default BusinessUserList;